import { compact, flatMap, join, size } from 'es-toolkit/compat';
import { CredentialMode, ItNote } from './cliInstructionTypes';

export const AWS_CLI_INSTALL_DOCS_URL = 'https://docs.aws.amazon.com/cli/latest/userguide/getting-started-install.html';

export const AWS_PROFILE_NAME = 'nucleai-upload';
export const AWS_CLI_ACCELERATED_ENDPOINT_OPTION = '--endpoint-url https://s3-accelerate.amazonaws.com';

const S3_DESTINATION_PATTERN = /^s3:\/\/([^/]+)(?:\/.*)?$/;
const LAB_UPLOAD_PREFIX = 'ATOM';

const INSTALL_IT_MESSAGE =
  'Please install or approve AWS CLI version 2 so I can upload slides to Nucleai.\n\nInstaller source: https://docs.aws.amazon.com/cli/latest/userguide/getting-started-install.html\n\nThe computer also needs outbound HTTPS access to AWS S3, the S3 Transfer Acceleration endpoint https://s3-accelerate.amazonaws.com, and AWS STS.';

const getCustomerUploadIdentityPolicyJson = (destination: string) => {
  const destinationMatch = S3_DESTINATION_PATTERN.exec(destination);
  const destinationBucket = destinationMatch?.[1] || 'NUCLEAI_DESTINATION_BUCKET';

  return JSON.stringify(
    {
      Version: '2012-10-17',
      Statement: [
        {
          Sid: 'AllowNucleaiUploadSourceRead',
          Effect: 'Allow',
          Action: ['s3:GetBucketLocation', 's3:ListBucket', 's3:ListBucketMultipartUploads'],
          Resource: ['arn:aws:s3:::SOURCE_BUCKET_NAME'],
        },
        {
          Sid: 'AllowNucleaiUploadObjectRead',
          Effect: 'Allow',
          Action: ['s3:GetObject', 's3:GetObjectAttributes', 's3:GetObjectTagging'],
          Resource: ['arn:aws:s3:::SOURCE_BUCKET_NAME/SOURCE_PREFIX/*'],
        },
        {
          Sid: 'AllowNucleaiUploadDestinationWrite',
          Effect: 'Allow',
          Action: ['s3:PutObject', 's3:AbortMultipartUpload', 's3:ListMultipartUploadParts', 's3:PutObjectAcl'],
          Resource: [`arn:aws:s3:::${destinationBucket}/${LAB_UPLOAD_PREFIX}/*`],
        },
      ],
    },
    null,
    2
  );
};

const DESTINATION_BUCKET_GRANTS_SUMMARY =
  "After the IAM role or user ARN is added in Nucleai, Nucleai configures each study destination. Your AWS administrator does not need to modify the Nucleai bucket, but must attach an identity policy that allows the role or user to write under the lab's ATOM folder. This lab-level permission can be approved once and reused for future studies. Nucleai validation cannot inspect policies in your AWS account.";

const SOURCE_KMS_PERMISSIONS_SUMMARY =
  'If the source bucket uses SSE-KMS, the same AWS identity must also have kms:Decrypt and kms:DescribeKey on the source KMS key.';

export const getItNotes = (
  credentialMode: CredentialMode,
  destination = ''
): Record<'install' | 'connect' | 'upload', ItNote | undefined> => ({
  install: {
    title: 'IT note for AWS CLI installation',
    body: 'Install or approve AWS CLI version 2 from the official AWS installer page. The computer also needs outbound HTTPS access to AWS S3, the S3 Transfer Acceleration endpoint, and AWS STS.',
    message: INSTALL_IT_MESSAGE,
  },
  connect:
    credentialMode === 'customer-aws'
      ? {
          title: 'IT note for AWS role or user access',
          body: 'Ask your AWS administrator for the IAM role ARN or IAM user ARN that should upload slides.',
          message:
            'Please provide the IAM role ARN or IAM user ARN that should upload slides to Nucleai.\n\nAfter this ARN is added in Nucleai, Nucleai completes the upload setup.',
        }
      : undefined,
  upload:
    credentialMode === 'customer-aws'
      ? {
          title: 'IT note for S3 bucket permissions',
          body: `The AWS identity used for uploads needs an IAM identity policy with the required source bucket and object operations, plus object uploads under the lab's ATOM folder in the Nucleai destination bucket. ${SOURCE_KMS_PERMISSIONS_SUMMARY} ${DESTINATION_BUCKET_GRANTS_SUMMARY} Network controls should allow AWS S3, and may need the S3 Transfer Acceleration endpoint for local file uploads.`,
          message: `Please attach an IAM identity policy to the AWS role or user used for uploads. It must allow the source bucket and object operations in the template below, plus object uploads under the lab's ATOM folder in the Nucleai destination bucket.\n\n${SOURCE_KMS_PERMISSIONS_SUMMARY}\n\n${DESTINATION_BUCKET_GRANTS_SUMMARY}\n\nFor local file uploads, generated commands use ${AWS_CLI_ACCELERATED_ENDPOINT_OPTION}, so network controls should allow https://s3-accelerate.amazonaws.com.\n\nSuggested IAM identity policy template (replace SOURCE_BUCKET_NAME and SOURCE_PREFIX):\n${getCustomerUploadIdentityPolicyJson(
            destination
          )}`,
        }
      : undefined,
});

export const buildItEmailBody = (
  itNotes: Record<'install' | 'connect' | 'upload', ItNote | undefined>,
  credentialMode: CredentialMode
) => {
  const orderedNotes = compact([itNotes.install, itNotes.connect, itNotes.upload]);
  return join(
    [
      'Hello,',
      '',
      `I need help preparing my computer and AWS access so I can upload slides to Nucleai with AWS CLI ${
        credentialMode === 'temporary-nucleai' ? 'from my computer' : 'from our S3 bucket'
      }.`,
      '',
      ...flatMap(orderedNotes, (note, index) => [
        `${size(orderedNotes) > 1 ? `${index + 1}. ` : ''}${note!.title.replace('IT note for ', '')}`,
        note!.message,
        '',
      ]),
      'Thank you.',
    ],
    '\n'
  );
};

export const getItEmailSubject = (credentialMode: CredentialMode) =>
  credentialMode === 'temporary-nucleai'
    ? 'IT request: AWS CLI setup for Nucleai slide upload from my computer'
    : 'IT request: AWS CLI and IAM setup for Nucleai slide upload from our S3 bucket';
